import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Building2, Users, Share2, Clock, ArrowRight, ChevronRight, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import CreditTrackerCard from "@/components/CreditTrackerCard";
import ReferralRoiCard from "@/components/ReferralRoiCard";
import { useAuth } from "@/contexts/AuthContext";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [deletionAlerts, setDeletionAlerts] = useState([]);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const load = async () => {
    try {
      const { data } = await api.get("/stats");
      setStats(data);
    } catch (e) {
      toast.error("Failed to load stats", { description: formatApiError(e) });
    }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/deletion-alerts");
        setDeletionAlerts(data.alerts || []);
      } catch {
        // non-critical
      }
    })();
  }, [isAdmin]);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {isAdmin && deletionAlerts.length > 0 ? (
        <div
          className="rounded-lg border border-amber-300 bg-amber-50 p-4 flex flex-col sm:flex-row sm:items-center gap-3"
          data-testid="deletion-alerts-banner"
        >
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-amber-900">
              Unusual deletion activity in the last 24 hours
            </div>
            <div className="text-xs text-amber-800 mt-0.5">
              {deletionAlerts.map((a) => `${a.email} deleted ${a.count} records`).join(" · ")}
            </div>
          </div>
          <Link to="/recycle-bin" className="shrink-0">
            <Button
              variant="outline"
              size="sm"
              className="border-amber-400 text-amber-900 hover:bg-amber-100"
              data-testid="deletion-alerts-review-button"
            >
              Review in Master Recycle Bin <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
            </Button>
          </Link>
        </div>
      ) : null}
      <div className="flex flex-col gap-1">
        <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">Overview</p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-display">
          Realtor Outreach CRM
        </h1>
        <p className="text-sm text-slate-600 max-w-2xl">
          Import agent rosters straight from Realtor.ca, enrich their social
          profiles, keep brokerages tied to the right office, then run
          outreach and export polished vCards.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard
          title="Realtors"
          value={stats?.realtors}
          icon={Users}
          link="/realtors"
          testid="stat-realtors"
        />
        <StatCard
          title="Brokerages"
          value={stats?.brokerages_approved}
          subtitle={stats ? `${stats.brokerages_candidates} candidates` : null}
          icon={Building2}
          link="/brokerages"
          testid="stat-brokerages"
        />
        <StatCard
          title="With socials"
          value={stats?.socials_filled}
          icon={Share2}
          subtitle={
            stats?.realtors > 0
              ? `${Math.round(
                  (100 * (stats.socials_filled || 0)) / Math.max(1, stats.realtors)
                )}% coverage`
              : null
          }
          testid="stat-socials"
        />
        <StatCard
          title="Running jobs"
          value={stats?.running_jobs}
          icon={Clock}
          link="/scrape-history"
          testid="stat-running-jobs"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ActionTile
          title="1 · Import Realtors"
          body="Bulk-import agent rosters from Realtor.ca by URL or search. This is the highest-quality, most consistent starting point for new locations."
          to="/import-realtor-ca"
          cta="Open importer"
          testid="action-import"
        />
        <ActionTile
          title="2 · Scrape & enrich realtors"
          body="Enrich imported agents with social profiles, headshots, and brokerage links so their cards are ready for outreach."
          to="/realtors"
          cta="Open realtors"
          testid="action-realtors"
        />
        <ActionTile
          title="3 · Brokerages"
          body="Review tracked brokerages and keep every realtor tied to the correct office. Add or approve brokerages as your roster grows."
          to="/brokerages"
          cta="Open brokerages"
          testid="action-brokerages"
        />
        <ActionTile
          title="4 · Outreach"
          body="Send follow-ups, log interactions, and export polished vCards for the referral partners who actually send you business."
          to="/outreach"
          cta="Open outreach"
          testid="action-outreach"
        />
      </div>

      {isAdmin ? <CreditTrackerCard /> : null}

      {/* Referral partner ROI — user-scoped leaderboard of who's actually
          sending you inspection business (with QoQ momentum) so the
          inspector knows who deserves a Christmas card, a lunch, or a
          warm reconnect nudge before someone else steals the account. */}
      <ReferralRoiCard />

      <LastJobCard job={stats?.last_job} />
    </div>
  );
}

function StatCard({ title, value, subtitle, icon: Icon, link, testid }) {
  const body = (
    <div
      className="rounded-lg border border-slate-200 bg-white p-4 hover:border-slate-300 transition-colors h-full"
      data-testid={testid}
    >
      <div className="flex items-start justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-slate-500">{title}</span>
        <Icon className="h-4 w-4 text-slate-400" aria-hidden="true" />
      </div>
      <div className="mt-3 flex items-end gap-2">
        {value === undefined || value === null ? (
          <Skeleton className="h-8 w-16" />
        ) : (
          <span className="text-3xl font-bold tracking-tight text-slate-900 tabular-nums font-display">
            {value}
          </span>
        )}
      </div>
      {subtitle ? (
        <div className="text-xs text-slate-500 mt-1">{subtitle}</div>
      ) : null}
      {link ? (
        <div className="flex items-center gap-1 text-xs text-[hsl(var(--brand-strong))] mt-3 font-medium">
          Open <ChevronRight className="h-3 w-3" />
        </div>
      ) : null}
    </div>
  );
  return link ? (
    <Link to={link} className="block">
      {body}
    </Link>
  ) : (
    body
  );
}

function ActionTile({ title, body, to, cta, testid }) {
  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-5 flex flex-col gap-3"
      data-testid={testid}
    >
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">Action</div>
        <h3 className="text-lg font-semibold text-slate-900 font-display mt-1">{title}</h3>
        <p className="text-sm text-slate-600 mt-1">{body}</p>
      </div>
      <div className="mt-auto">
        <Button asChild variant="outline" className="min-h-[44px]">
          <Link to={to} className="flex items-center gap-2">
            {cta} <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </div>
    </div>
  );
}

function LastJobCard({ job }) {
  if (!job) {
    return (
      <div
        className="rounded-lg border border-dashed border-slate-200 bg-white p-5 text-sm text-slate-500"
        data-testid="last-job-card"
      >
        No scrape jobs yet. Head to{" "}
        <Link to="/brokerages" className="text-[hsl(var(--brand-strong))] font-medium underline-offset-4 hover:underline">
          Brokerages
        </Link>{" "}
        to run your first scrape.
      </div>
    );
  }
  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-5"
      data-testid="last-job-card"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">
            Last job
          </div>
          <div className="text-sm font-medium text-slate-900 mt-0.5">
            {job.type} — {job.target_label || "—"}
          </div>
        </div>
        <Status status={job.status} />
      </div>
      <div className="mt-3 text-xs text-slate-500">
        Started {fmtTime(job.started_at)}
        {job.finished_at ? ` · finished ${fmtTime(job.finished_at)}` : null}
      </div>
      {job.summary ? (
        <pre className="mt-3 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2 overflow-x-auto">
{JSON.stringify(job.summary, null, 2)}
        </pre>
      ) : null}
    </div>
  );
}

function Status({ status }) {
  const map = {
    done: "bg-emerald-100 text-emerald-800 border-emerald-200",
    failed: "bg-red-100 text-red-800 border-red-200",
    running: "bg-amber-100 text-amber-800 border-amber-200",
    queued: "bg-slate-100 text-slate-700 border-slate-200",
  };
  return (
    <span className={`px-2 py-0.5 text-xs rounded border ${map[status] || map.queued}`}>
      {status}
    </span>
  );
}

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso || "";
  }
}
